/**
 * Payments app — status + transaction catalog (client).
 *
 * Status is DERIVED on the server from a student's payment_transactions ledger plus the
 * charge's tracking mode (see api/constants/payments.js → deriveStatus). The client renders
 * the derived status and, where a charge defines `display` rules, maps it per surface
 * (admin roster vs. profile) — e.g. showing a partial payment as a plain "No" on a family-
 * facing profile while admins still see "Partial".
 */
export const PAYMENT_STATUS = {
  UNPAID: 'unpaid',
  OVERDUE: 'overdue',
  PARTIAL: 'partial',
  PAID: 'paid',
  REFUNDED: 'refunded',
  NOT_APPLICABLE: 'not_applicable',
  PAYMENT_FAILURE: 'payment_failure',
  INSUFFICIENT_FUNDS: 'insufficient_funds'
};

export const DEFAULT_PAYMENT_STATUS = PAYMENT_STATUS.UNPAID;

/** Ordered for selects and legends. */
export const PAYMENT_STATUS_OPTIONS = [
  { value: PAYMENT_STATUS.UNPAID, label: 'Unpaid', color: '#92400e', bg: '#fef3c7', border: '#fde68a' },
  {
    value: PAYMENT_STATUS.OVERDUE,
    label: 'Overdue',
    color: '#9f1239',
    bg: '#ffe4e6',
    border: '#fecdd3'
  },
  { value: PAYMENT_STATUS.PARTIAL, label: 'Partial', color: '#9a3412', bg: '#ffedd5', border: '#fde7aa' },
  { value: PAYMENT_STATUS.PAID, label: 'Paid', color: '#166534', bg: '#dcfce7', border: '#bbf7d0' },
  { value: PAYMENT_STATUS.REFUNDED, label: 'Refunded', color: '#1e40af', bg: '#dbeafe', border: '#bfdbfe' },
  { value: PAYMENT_STATUS.NOT_APPLICABLE, label: 'Not Applicable', color: '#475569', bg: '#f1f5f9', border: '#e2e8f0' },
  { value: PAYMENT_STATUS.PAYMENT_FAILURE, label: 'Payment Failure', color: '#991b1b', bg: '#fee2e2', border: '#fecaca' },
  { value: PAYMENT_STATUS.INSUFFICIENT_FUNDS, label: 'Insufficient Funds', color: '#9a3412', bg: '#ffedd5', border: '#fed7aa' }
];

/**
 * Statuses staff can set with one click (mapped to append-only ledger ops via
 * /api/payments/quick-set). Each action inserts a transaction; status is derived
 * from the full history. Nothing is deleted.
 */
export const QUICK_SET_STATUSES = [
  PAYMENT_STATUS.PAID,
  PAYMENT_STATUS.PARTIAL,
  PAYMENT_STATUS.UNPAID,
  PAYMENT_STATUS.NOT_APPLICABLE,
  PAYMENT_STATUS.PAYMENT_FAILURE,
  PAYMENT_STATUS.INSUFFICIENT_FUNDS
];

const BY_VALUE = PAYMENT_STATUS_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o;
  return acc;
}, {});

export function getPaymentStatusMeta(value) {
  const key = String(value || DEFAULT_PAYMENT_STATUS)
    .trim()
    .toLowerCase();
  return BY_VALUE[key] || BY_VALUE[DEFAULT_PAYMENT_STATUS];
}

export function getPaymentStatusLabel(value) {
  return getPaymentStatusMeta(value).label;
}

/** True when required_by_date (YYYY-MM-DD) is before today (local calendar). */
export function isPastDueDate(requiredByDate, now = new Date()) {
  const raw = String(requiredByDate || '').trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return false;
  const due = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return today > due;
}

/**
 * Roster/profile display status — overdue is derived, not stored on the ledger.
 * Unpaid or partial + past required_by_date → overdue.
 */
export function resolveRosterDisplayStatus(derivedStatus, requiredByDate) {
  const status = String(derivedStatus || DEFAULT_PAYMENT_STATUS).toLowerCase();
  if (
    (status === PAYMENT_STATUS.UNPAID || status === PAYMENT_STATUS.PARTIAL) &&
    isPastDueDate(requiredByDate)
  ) {
    return PAYMENT_STATUS.OVERDUE;
  }
  return status;
}

/** Yes/no charges (no dollar amount) — eligible for roster ✓ / ✗ quick-set. */
export function isBooleanCharge(cell) {
  if (!cell) return false;
  if (cell.tracking_mode === 'boolean') return true;
  const due = cell.amount_due ?? cell.amount;
  return due == null || Number(due) === 0;
}

export function supportsRosterQuickSet(cell) {
  if (!cell?.requirement_id) return false;
  const status = String(cell.status || '').toLowerCase();
  if (status === PAYMENT_STATUS.PAID || status === PAYMENT_STATUS.NOT_APPLICABLE) return false;
  return isBooleanCharge(cell);
}

/* ----------------------------- transactions ----------------------------- */

export const TXN_TYPE = {
  PAYMENT: 'payment',
  REFUND: 'refund',
  ADJUSTMENT: 'adjustment',
  FAILED_ATTEMPT: 'failed_attempt',
  WAIVER: 'waiver'
};

export const TXN_TYPE_OPTIONS = [
  { value: TXN_TYPE.PAYMENT, label: 'Payment' },
  { value: TXN_TYPE.REFUND, label: 'Refund' },
  { value: TXN_TYPE.ADJUSTMENT, label: 'Adjustment' },
  { value: TXN_TYPE.FAILED_ATTEMPT, label: 'Failed attempt' },
  { value: TXN_TYPE.WAIVER, label: 'Waiver (N/A)' }
];

export const PAYMENT_METHOD_OPTIONS = [
  { value: '', label: '—' },
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'card', label: 'Card' },
  { value: 'ach', label: 'ACH / bank' },
  { value: 'other', label: 'Other' }
];

export const FAILURE_REASON_OPTIONS = [
  { value: 'payment_failure', label: 'Payment failure' },
  { value: 'insufficient_funds', label: 'Insufficient funds' }
];

export function getTxnTypeLabel(value) {
  const o = TXN_TYPE_OPTIONS.find((t) => t.value === value);
  return o ? o.label : String(value || '');
}

/** Rare ledger entries — shown only when staff expands "Refund or adjustment". */
export const CORRECTION_TXN_TYPES = [
  { value: TXN_TYPE.REFUND, label: 'Refund (money returned)' },
  { value: TXN_TYPE.ADJUSTMENT, label: 'Adjustment (manual correction)' }
];

/** @deprecated Use CORRECTION_TXN_TYPES; payment is the default record form. */
export const RECORD_TXN_TABS = [
  { value: TXN_TYPE.PAYMENT, label: 'Payment received', short: 'Received' },
  { value: TXN_TYPE.REFUND, label: 'Refund', short: 'Refund' },
  { value: TXN_TYPE.ADJUSTMENT, label: 'Adjustment', short: 'Adjust' }
];

/** One-click status actions shown in the charge editor (maps to /api/payments/quick-set). */
export const QUICK_ACTIONS = [
  {
    status: PAYMENT_STATUS.PAID,
    label: 'Mark as paid',
    hint: 'Records a payment for any remaining balance (history is kept)',
    primary: true
  },
  {
    status: PAYMENT_STATUS.PARTIAL,
    label: 'Record partial',
    hint: 'Appends a partial payment; prior attempts and payments stay on the ledger',
    secondary: true
  },
  {
    status: PAYMENT_STATUS.UNPAID,
    label: 'Reset to unpaid',
    hint: 'Appends an adjustment to zero the balance (history is kept)',
    destructive: true
  },
  {
    status: PAYMENT_STATUS.NOT_APPLICABLE,
    label: 'Waive (not applicable)',
    hint: 'Appends a waiver; prior transactions remain on the ledger'
  },
  {
    status: PAYMENT_STATUS.PAYMENT_FAILURE,
    label: 'Payment failed',
    hint: 'Appends a failed-attempt record',
    secondary: true
  },
  {
    status: PAYMENT_STATUS.INSUFFICIENT_FUNDS,
    label: 'Insufficient funds',
    hint: 'Appends an insufficient-funds attempt',
    secondary: true
  }
];

export function todayDateInput() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Value for `<input type="datetime-local" />` — current local date/time. */
export function nowDateTimeLocalInput() {
  const d = new Date();
  return (
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T` +
    `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  );
}

/** Parse datetime-local (no TZ) or ISO string as a Date in the browser's local zone. */
export function parseTxnDateTime(value) {
  if (!value) return null;
  const str = String(value).trim();
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (m && !/[zZ]$/.test(str) && !/[+-]\d{2}/.test(str.slice(10))) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), 0, 0);
  }
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Convert datetime-local or Date to ISO UTC for API / timestamptz storage. */
export function dateTimeLocalToStorage(value) {
  const d = parseTxnDateTime(value);
  return d ? d.toISOString() : null;
}

/** Coerce a stored date/time to `datetime-local` input value (browser local wall clock). */
export function toDateTimeLocalInput(value) {
  if (!value) return nowDateTimeLocalInput();
  const d = parseTxnDateTime(value) || new Date(value);
  if (Number.isNaN(d.getTime())) return nowDateTimeLocalInput();
  return (
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T` +
    `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  );
}

/** Display: mm/dd/yyyy hh:mm AM/PM (browser local). */
export function formatTxnDateTime(value) {
  if (!value) return '—';
  const d = parseTxnDateTime(value) || new Date(value);
  if (Number.isNaN(d.getTime())) {
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return String(value);
    return formatTxnDateTime(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0));
  }
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  let hours = d.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${yyyy} ${hours}:${mins} ${ampm}`;
}

export function createEmptyTxnDraft(defaults = {}) {
  return {
    txn_type: TXN_TYPE.PAYMENT,
    amount: '',
    method: '',
    occurred_on: nowDateTimeLocalInput(),
    notes: '',
    is_partial: false,
    reason: null,
    ...defaults
  };
}

/** UI-facing transaction kinds (badge picker in editor). */
export const TXN_KIND = {
  FULL_PAYMENT: 'full_payment',
  PARTIAL_PAYMENT: 'partial_payment',
  REFUND: 'refund',
  FAILED_ATTEMPT: 'failed_attempt',
  INSUFFICIENT_FUNDS: 'insufficient_funds'
};

export const TXN_KIND_OPTIONS = [
  { value: TXN_KIND.FULL_PAYMENT, label: 'Final/Full Payment' },
  { value: TXN_KIND.PARTIAL_PAYMENT, label: 'Partial Payment' },
  { value: TXN_KIND.REFUND, label: 'Refund' },
  { value: TXN_KIND.FAILED_ATTEMPT, label: 'Failed Attempt' },
  { value: TXN_KIND.INSUFFICIENT_FUNDS, label: 'Insufficient Funds' }
];

export function getTxnKindLabel(kind) {
  const o = TXN_KIND_OPTIONS.find((t) => t.value === kind);
  return o ? o.label : kind;
}

export function txnKindToFields(kind) {
  switch (kind) {
    case TXN_KIND.PARTIAL_PAYMENT:
      return { txn_type: TXN_TYPE.PAYMENT, is_partial: true, reason: null };
    case TXN_KIND.REFUND:
      return { txn_type: TXN_TYPE.REFUND, is_partial: false, reason: null };
    case TXN_KIND.FAILED_ATTEMPT:
      return { txn_type: TXN_TYPE.FAILED_ATTEMPT, is_partial: false, reason: 'payment_failure' };
    case TXN_KIND.INSUFFICIENT_FUNDS:
      return { txn_type: TXN_TYPE.FAILED_ATTEMPT, is_partial: false, reason: 'insufficient_funds' };
    case TXN_KIND.FULL_PAYMENT:
    default:
      return { txn_type: TXN_TYPE.PAYMENT, is_partial: false, reason: null };
  }
}

export function draftToTxnKind(draft) {
  if (draft?.txn_type === TXN_TYPE.REFUND) return TXN_KIND.REFUND;
  if (draft?.txn_type === TXN_TYPE.FAILED_ATTEMPT) {
    return draft.reason === 'insufficient_funds' ? TXN_KIND.INSUFFICIENT_FUNDS : TXN_KIND.FAILED_ATTEMPT;
  }
  if (draft?.txn_type === TXN_TYPE.PAYMENT) {
    return draft.is_partial ? TXN_KIND.PARTIAL_PAYMENT : TXN_KIND.FULL_PAYMENT;
  }
  return TXN_KIND.FULL_PAYMENT;
}

export function txnToKind(txn) {
  if (!txn) return TXN_KIND.FULL_PAYMENT;
  if (txn.txn_type === TXN_TYPE.WAIVER) return null;
  if (txn.txn_type === TXN_TYPE.REFUND) return TXN_KIND.REFUND;
  if (txn.txn_type === TXN_TYPE.FAILED_ATTEMPT) {
    return txn.reason === 'insufficient_funds' ? TXN_KIND.INSUFFICIENT_FUNDS : TXN_KIND.FAILED_ATTEMPT;
  }
  if (txn.txn_type === TXN_TYPE.PAYMENT) {
    return isTxnPartial(txn) ? TXN_KIND.PARTIAL_PAYMENT : TXN_KIND.FULL_PAYMENT;
  }
  return null;
}

/** Charge-level status picker (clickable status badge). */
export const STATUS_PICKER_OPTIONS = [
  { status: PAYMENT_STATUS.PAID, label: 'Paid' },
  { status: PAYMENT_STATUS.PARTIAL, label: 'Partial', action: 'partial' },
  { status: PAYMENT_STATUS.UNPAID, label: 'Unpaid', destructive: true },
  { status: PAYMENT_STATUS.NOT_APPLICABLE, label: 'Waive' }
];

/** Whether a payment transaction was recorded as not fully settled. */
export function isTxnPartial(txn) {
  if (txn?.txn_type !== TXN_TYPE.PAYMENT) return false;
  const meta = txn.metadata && typeof txn.metadata === 'object' ? txn.metadata : {};
  return meta.is_partial === true;
}

/** Mirror of the server's deriveStatus (api/constants/payments.js) for previewing changes. */
export function deriveStatusClient(transactions, charge = {}) {
  const txns = transactions || [];
  const mode =
    charge.tracking_mode === 'amount'
      ? 'amount'
      : charge.tracking_mode === 'boolean'
        ? 'boolean'
        : (charge.amount_due ?? charge.amount) != null
          ? 'amount'
          : 'boolean';
  const due = Number(charge.amount_due ?? charge.amount);
  const net = computeNetFromTransactions(txns);

  if (txns.some((t) => t.txn_type === TXN_TYPE.WAIVER)) return PAYMENT_STATUS.NOT_APPLICABLE;

  // Any charge with a positive dollar amount settles by net vs due.
  if (Number.isFinite(due) && due > 0) {
    if (net >= due) return PAYMENT_STATUS.PAID;
    if (net > 0) return PAYMENT_STATUS.PARTIAL;
    if (txns.some(isTxnPartial)) return PAYMENT_STATUS.PARTIAL;
    if (net <= 0 && txns.some((t) => t.txn_type === TXN_TYPE.REFUND)) return PAYMENT_STATUS.REFUNDED;
  } else if (mode === 'amount') {
    if (net > 0) return PAYMENT_STATUS.PARTIAL;
    if (txns.some(isTxnPartial)) return PAYMENT_STATUS.PARTIAL;
    if (net <= 0 && txns.some((t) => t.txn_type === TXN_TYPE.REFUND)) return PAYMENT_STATUS.REFUNDED;
  } else {
    if (txns.some((t) => t.txn_type === TXN_TYPE.PAYMENT && !isTxnPartial(t))) return PAYMENT_STATUS.PAID;
    if (txns.some(isTxnPartial)) return PAYMENT_STATUS.PARTIAL;
    if (txns.some((t) => t.txn_type === TXN_TYPE.PAYMENT)) return PAYMENT_STATUS.PAID;
  }

  const fail = txns
    .filter((t) => t.txn_type === TXN_TYPE.FAILED_ATTEMPT)
    .sort((a, b) => {
      const da = a.occurred_on ? new Date(a.occurred_on).getTime() : 0;
      const db = b.occurred_on ? new Date(b.occurred_on).getTime() : 0;
      if (da !== db) return db - da;
      const ca = a.created_at ? new Date(a.created_at).getTime() : 0;
      const cb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return cb - ca;
    })[0];
  if (fail) {
    return fail.reason === 'insufficient_funds'
      ? PAYMENT_STATUS.INSUFFICIENT_FUNDS
      : PAYMENT_STATUS.PAYMENT_FAILURE;
  }
  return PAYMENT_STATUS.UNPAID;
}

/** Coarse settlement rank used to detect a status downgrade (e.g. refund undoing Paid). */
export function statusSettlementRank(status) {
  switch (status) {
    case PAYMENT_STATUS.PAID:
      return 3;
    case PAYMENT_STATUS.PARTIAL:
      return 2;
    default:
      return 1;
  }
}

/** Build a minimal txn-like object from a draft, for client-side status projection. */
export function draftToProjectedTxn(draft, kindOverride = null) {
  const kind = kindOverride ?? draftToTxnKind(draft);
  const fields = txnKindToFields(kind);
  return {
    txn_type: fields.txn_type,
    amount: draft.amount === '' || draft.amount == null ? null : Number(draft.amount),
    reason: fields.reason,
    metadata: { is_partial: fields.is_partial }
  };
}

/**
 * Amount mode: pick Full vs Partial from cumulative net (existing txns + draft amount) vs due.
 * Boolean mode: keep the user's selected payment kind.
 */
export function resolveDraftPaymentKind(draft, { transactions = [], charge = {} } = {}) {
  const selected = draftToTxnKind(draft);
  if (selected !== TXN_KIND.FULL_PAYMENT && selected !== TXN_KIND.PARTIAL_PAYMENT) {
    return selected;
  }

  const isAmountMode =
    charge.tracking_mode === 'amount' ||
    (charge.tracking_mode !== 'boolean' && (charge.amount_due ?? charge.amount) != null);

  if (!isAmountMode) return selected;

  const amt = draft.amount === '' || draft.amount == null ? null : Number(draft.amount);
  if (amt == null || !Number.isFinite(amt) || amt <= 0) return selected;

  const due = Number(charge.amount_due ?? charge.amount);
  if (!Number.isFinite(due) || due <= 0) return selected;

  const net = computeNetFromTransactions(transactions);
  const afterNet = Number((net + amt).toFixed(2));

  if (afterNet >= due) return TXN_KIND.FULL_PAYMENT;
  return TXN_KIND.PARTIAL_PAYMENT;
}

/**
 * Validate a draft transaction against the charge ledger (runs on amount/type change and save).
 * Returns a notice for inline display and whether save is allowed.
 */
export function validateDraftTransaction(draft, { transactions = [], charge = {}, currentStatus, currency = 'USD' } = {}) {
  if (!draft || draft.readOnly) return { canSave: false, notice: null };

  const selectedKind = draftToTxnKind(draft);
  const isMoney =
    selectedKind === TXN_KIND.FULL_PAYMENT ||
    selectedKind === TXN_KIND.PARTIAL_PAYMENT ||
    selectedKind === TXN_KIND.REFUND;
  if (!isMoney) return { canSave: true, notice: null };

  const amt = draft.amount === '' || draft.amount == null ? null : Number(draft.amount);
  const hasAmount = amt != null && Number.isFinite(amt) && amt > 0;
  const due = Number(charge.amount_due ?? charge.amount);
  const hasDue = Number.isFinite(due) && due > 0;

  const needsAmount =
    hasDue &&
    (selectedKind === TXN_KIND.FULL_PAYMENT ||
      selectedKind === TXN_KIND.PARTIAL_PAYMENT ||
      selectedKind === TXN_KIND.REFUND);

  if (needsAmount && !hasAmount) {
    return { canSave: false, notice: { tone: 'info', text: 'Enter an amount to continue.' } };
  }

  const net = computeNetFromTransactions(transactions);
  let afterNet = net;
  if (hasAmount) {
    if (selectedKind === TXN_KIND.FULL_PAYMENT || selectedKind === TXN_KIND.PARTIAL_PAYMENT) {
      afterNet = Number((net + amt).toFixed(2));
    } else if (selectedKind === TXN_KIND.REFUND) {
      afterNet = Number((net - amt).toFixed(2));
    }
  }

  const projectedTxn = draftToProjectedTxn(draft, selectedKind);
  const nextTxns = [...transactions, projectedTxn];
  const projected = deriveStatusClient(nextTxns, charge);
  const curLabel = getPaymentStatusLabel(currentStatus);
  const projLabel = getPaymentStatusLabel(projected);

  // (1) Partial selected but cumulative total meets/exceeds charge → use Full/Final.
  if (hasDue && hasAmount && selectedKind === TXN_KIND.PARTIAL_PAYMENT && afterNet >= due) {
    return {
      canSave: false,
      notice: {
        tone: 'warning',
        action: 'switch_full',
        text:
          `Total would be ${formatPaymentAmount(afterNet, currency)} — meeting the ` +
          `${formatPaymentAmount(due, currency)} charge. Use Final/Full Payment.`
      }
    };
  }

  // (2) Full/Final selected but cumulative total is still below charge → use Partial.
  if (hasDue && hasAmount && selectedKind === TXN_KIND.FULL_PAYMENT && afterNet < due) {
    const short = Number((due - afterNet).toFixed(2));
    return {
      canSave: false,
      notice: {
        tone: 'warning',
        action: 'switch_partial',
        text:
          `Total would be ${formatPaymentAmount(afterNet, currency)} — ` +
          `${formatPaymentAmount(short, currency)} short of ${formatPaymentAmount(due, currency)}. ` +
          `This cannot be Final/Full Payment. Use Partial Payment.`
      }
    };
  }

  // (3) Refund drops net below charge amount — warn status downgrade from Paid/Partial.
  if (selectedKind === TXN_KIND.REFUND && hasAmount) {
    const downgrade =
      statusSettlementRank(projected) < statusSettlementRank(currentStatus) &&
      (currentStatus === PAYMENT_STATUS.PAID || currentStatus === PAYMENT_STATUS.PARTIAL);
    if (downgrade) {
      return {
        canSave: true,
        confirmOnSave: true,
        projected,
        notice: {
          tone: 'warning',
          text:
            `This refund brings the total to ${formatPaymentAmount(afterNet, currency)}` +
            (hasDue ? ` (below ${formatPaymentAmount(due, currency)})` : '') +
            `. Status will change from ${curLabel} to ${projLabel}.`
        }
      };
    }
    return {
      canSave: true,
      notice: { tone: 'info', text: `After this refund the status stays ${projLabel}.` }
    };
  }

  if (selectedKind === TXN_KIND.FULL_PAYMENT && projected === PAYMENT_STATUS.PAID) {
    return {
      canSave: true,
      notice: {
        tone: 'success',
        text: hasDue
          ? `Meets ${formatPaymentAmount(due, currency)} — charge will be Paid.`
          : 'Charge will be Paid.'
      }
    };
  }

  if (selectedKind === TXN_KIND.PARTIAL_PAYMENT && projected === PAYMENT_STATUS.PARTIAL) {
    const remainingAfter = hasDue ? getRemainingDue(due, afterNet) : null;
    const tail =
      remainingAfter != null && remainingAfter > 0
        ? ` ${formatPaymentAmount(remainingAfter, currency)} will remain due.`
        : '';
    return { canSave: true, notice: { tone: 'info', text: `Partial payment — status will be Partial.${tail}` } };
  }

  return { canSave: true, notice: null };
}

export function computeNetFromTransactions(transactions) {
  return (transactions || []).reduce((sum, t) => {
    const amt = Number(t.amount) || 0;
    if (t.txn_type === TXN_TYPE.PAYMENT) return sum + amt;
    if (t.txn_type === TXN_TYPE.REFUND) return sum - amt;
    if (t.txn_type === TXN_TYPE.ADJUSTMENT) return sum + amt;
    return sum;
  }, 0);
}

export function getRemainingDue(amountDue, netPaid) {
  const due = Number(amountDue);
  const net = Number(netPaid) || 0;
  if (!Number.isFinite(due) || due <= 0) return null;
  return Math.max(0, Number((due - net).toFixed(2)));
}

/* ------------------------------- display -------------------------------- */

/**
 * Map a derived status to what a given surface should show, honoring optional per-charge
 * `display` rules from the schedule relationship metadata.
 *
 *   display[surface] = {
 *     status_style: 'full' | 'binary' | 'binary_strict',
 *     labels: { <status>: 'Custom label' },
 *     show_amount: boolean,
 *     show_failures_as: 'failed' | 'unpaid' | 'hidden'
 *   }
 *
 *  full          → show the real status (Partial, Refunded, …)
 *  binary        → any progress (paid/partial/refunded with net>0) reads as "Yes"
 *  binary_strict → only fully paid reads as "Yes"; partial stays "No"
 */
export function resolveDisplayStatus(status, display, surface = 'admin') {
  const rules = (display && display[surface]) || {};
  const style = rules.status_style || 'full';
  const customLabel = rules.labels && rules.labels[status];

  // Non-money markers pass through in every style (label still overridable).
  if (status === PAYMENT_STATUS.NOT_APPLICABLE) {
    return { value: status, label: customLabel || 'N/A', meta: getPaymentStatusMeta(status) };
  }
  if (status === PAYMENT_STATUS.PAYMENT_FAILURE || status === PAYMENT_STATUS.INSUFFICIENT_FUNDS) {
    const failAs = rules.show_failures_as || 'failed';
    if (failAs === 'failed') {
      return { value: status, label: customLabel || getPaymentStatusLabel(status), meta: getPaymentStatusMeta(status) };
    }
    const fallback = PAYMENT_STATUS.UNPAID;
    return {
      value: fallback,
      label: rules.labels?.[fallback] || (style === 'full' ? 'Unpaid' : 'No'),
      meta: getPaymentStatusMeta(fallback)
    };
  }

  if (style === 'full') {
    return { value: status, label: customLabel || getPaymentStatusLabel(status), meta: getPaymentStatusMeta(status) };
  }

  // Binary styles collapse to Yes / No.
  const isYes =
    style === 'binary_strict'
      ? status === PAYMENT_STATUS.PAID
      : status === PAYMENT_STATUS.PAID ||
        status === PAYMENT_STATUS.PARTIAL ||
        status === PAYMENT_STATUS.REFUNDED;
  const value = isYes ? PAYMENT_STATUS.PAID : PAYMENT_STATUS.UNPAID;
  return {
    value,
    label: (rules.labels && rules.labels[value]) || (isYes ? 'Yes' : 'No'),
    meta: getPaymentStatusMeta(value)
  };
}

/** Format an amount with a currency code (e.g. 50 → "$50.00"). Falls back to code prefix. */
export function formatPaymentAmount(amount, currency = 'USD') {
  if (amount == null || amount === '') return '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
}

export function formatMonthKeyLabel(monthKey) {
  const m = String(monthKey || '').match(/^(\d{4})-(\d{2})$/);
  if (!m) return String(monthKey || '');
  const names = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const idx = parseInt(m[2], 10) - 1;
  return `${names[idx] || m[2]} ${m[1]}`;
}

/** Short month label for compact grids, e.g. "Aug '26". */
export function formatMonthKeyShort(monthKey) {
  const m = String(monthKey || '').match(/^(\d{4})-(\d{2})$/);
  if (!m) return String(monthKey || '');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const idx = parseInt(m[2], 10) - 1;
  return `${names[idx] || m[2]} '${m[1].slice(2)}`;
}
